<template>
  <div class="timeslots-desktop">
    <button
      @click="toPreviousColumns"
      class="timeslots-desktop__chevron timeslots-desktop__chevron-left"
      aria-label="Vorherige Woche anzeigen"
      :disabled="isChevronLeftDisabled"
    >
      <IconChevronLeft
        aria-hidden="true"
        :class="{
          'timeslots-desktop__chevron--disabled': isChevronLeftDisabled,
        }"
      />
    </button>
    <button
      @click="toNextColumns"
      class="timeslots-desktop__chevron timeslots-desktop__chevron-right"
      aria-label="Nächste Woche anzeigen"
      :disabled="isChevronRightDisabled"
    >
      <IconChevronRight
        aria-hidden="true"
        :class="{
          'timeslots-desktop__chevron--disabled': isChevronRightDisabled,
        }"
      />
    </button>
    <div class="timeslots-desktop__data" role="region" aria-label="Terminauswahl">
      <div class="timeslots-desktop__column" v-for="(date, index) in dateColumns" :key="index">
        <h3 class="timeslots-desktop__column__header" :id="`date-header-${index}`">
          {{ convertDateHeader(date) }}
        </h3>
        <fieldset class="timeslots-desktop__column__body" :aria-labelledby="`date-header-${index}`">
          <legend class="sr-only">Verfügbare Termine für {{ convertDateHeader(date) }}</legend>
          <template v-for="timeslot in columnTimeslots(date)" :key="timeslot.id || timeslot[0].id">
            <el-popover
              v-if="Array.isArray(timeslot)"
              trigger="click"
              placement="bottom-start"
              :offset="4"
              :teleported="false"
              width="fit-content"
              @show="activeDropdown = timeslot[0].id"
              @hide="activeDropdown === timeslot[0].id ? (activeDropdown = null) : null"
            >
              <template #reference>
                <button
                  type="button"
                  class="timeslots-desktop__column__body__slot"
                  :class="{
                    'timeslots-desktop__column__body__slot--active':
                      activeDropdown === timeslot[0].id,
                    'timeslots-desktop__column__body__slot--selected': isDropdownSelected(timeslot),
                  }"
                  :aria-expanded="activeDropdown === timeslot[0].id"
                  :aria-haspopup="true"
                >
                  <span class="timeslots-desktop__column__body__slot--prefix"> ab </span>
                  {{
                    isDropdownSelected(timeslot)
                      ? minutesToHHMM(selectedTimeslot[0].von)
                      : minutesToHHMM(timeslot[0].von)
                  }}
                  <IconChevronUp
                    v-if="activeDropdown === timeslot[0].id"
                    size="16"
                    aria-hidden="true"
                  />
                  <span v-else class="timeslots-desktop__column__body__slot--suffix">
                    ({{ timeslot.length }})
                  </span>
                </button>
              </template>
              <TimeslotsDropdown :timeslots="timeslot" @dropdownSelection="handleSelectTimeslot" />
            </el-popover>
            <div v-else class="timeslots-desktop__column__body__radio">
              <input
                type="radio"
                :name="`timeslot-${date}`"
                :id="`timeslot-${date}-${timeslot.id}`"
                :value="timeslot.id"
                :checked="isSelectedTimeslot(timeslot)"
                @change="handleSelectTimeslot(timeslot)"
                :aria-invalid="isOverlappingTimeslot(timeslot)"
                class="timeslots-desktop__column__body__radio-input sr-only"
              />

              <label
                :for="`timeslot-${date}-${timeslot.id}`"
                class="timeslots-desktop__column__body__slot"
                :class="{
                  'timeslots-desktop__column__body__slot--selected': isSelectedTimeslot(timeslot),
                  'timeslots-desktop__column__body__slot--error': isOverlappingTimeslot(timeslot),
                }"
              >
                <IconCheck v-if="isSelectedTimeslot(timeslot)" aria-hidden="true" />
                {{ minutesToHHMM(timeslot.von) }}
                <span class="sr-only">Uhr, {{ convertDateHeader(date) }}</span>
              </label>
            </div>
          </template>
          <div class="timeslots-desktop__column__body__filler" aria-hidden="true" />
        </fieldset>
      </div>
    </div>
    <div class="timeslots-desktop__empty" v-if="noTimeslots" role="status" aria-live="polite">
      <IconCalendar color="#737380" aria-hidden="true" />
      <p class="timeslots-desktop__empty__text">
        Leider keine verfügbaren Termine in den angezeigten Tagen.<br />
        <span class="timeslots-desktop__empty__text--highlight"
          >Bitte öffnen Sie den Kalender und wählen Sie dort einen grau hinterlegten Tag</span
        ><br />
        oder wählen Sie einen anderen Stützpunkt.
      </p>
    </div>
  </div>
</template>

<script>
import {
  clusterTimeslots,
  convertDateFormat,
  getLastSunday,
  getNextDate,
  getNextSunday,
  getPreviousDate,
  minutesToHHMM,
} from '@/utils/format.js'
import { mapGetters } from 'vuex'

import TimeslotsDropdown from '@/components/Booking/TimeslotsDropdown.vue'
import IconCalendar from '@/components/icons/IconCalendar.vue'
import IconCheck from '@/components/icons/IconCheck.vue'
import IconChevronLeft from '@/components/icons/IconChevronLeft.vue'
import IconChevronRight from '@/components/icons/IconChevronRight.vue'
import IconChevronUp from '@/components/icons/IconChevronUp.vue'

export default {
  components: {
    IconCalendar,
    IconChevronLeft,
    IconChevronRight,
    IconChevronUp,
    TimeslotsDropdown,
    IconCheck,
  },
  props: {
    isDayFiltersSet: {
      type: Boolean,
      default: false,
    },
    filteredTimeslots: {
      type: Array,
      default: () => [],
    },
    overlappingTimeslots: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      activeDropdown: null,
      hasTimeslots: null,
    }
  },
  computed: {
    ...mapGetters([
      'firstDateOfWeek',
      'dayFiltersStartDates',
      'dayFiltersStartDatesIndex',
      'isTimeslotsLoading',
      'timeslots',
      'selectedTimeslot',
      'datesWithTimeslots',
    ]),
    dateColumns() {
      if (this.isDayFiltersSet) {
        return [...new Set(this.filteredTimeslots.map((timeslot) => timeslot.datum))]
      } else {
        const lastDate = this.filteredTimeslots[this.filteredTimeslots.length - 1]?.datum
        const hasSaturday = new Date(lastDate).getDay() === 6
        let datesOfWeek = [
          getNextDate(this.firstDateOfWeek, 1),
          getNextDate(this.firstDateOfWeek, 2),
          getNextDate(this.firstDateOfWeek, 3),
          getNextDate(this.firstDateOfWeek, 4),
          getNextDate(this.firstDateOfWeek, 5),
        ]
        if (hasSaturday) datesOfWeek.push(getNextDate(this.firstDateOfWeek, 6))
        return datesOfWeek
      }
    },
    // filteredTimeslots > unstackedTimeslots > clusteredTimeslots
    unstackedTimeslots() {
      // show only first timeslot if service is stacked
      return this.filteredTimeslots.filter((timeslot) => timeslot.sequenceNumber !== 2)
    },
    noTimeslots() {
      // completely no timeslots from API (which looks 14 days ahead) OR just not on current week/filter
      return this.hasTimeslots === false || (this.hasTimeslots && !this.unstackedTimeslots.length)
    },
    isChevronLeftDisabled() {
      if (this.isDayFiltersSet) {
        return this.isTimeslotsLoading || this.dayFiltersStartDatesIndex === 0
      } else {
        return (
          this.isTimeslotsLoading ||
          this.firstDateOfWeek < convertDateFormat(new Date()) ||
          this.datesWithTimeslots.findIndex((date) => date < this.firstDateOfWeek) < 0 // if current week has no timeslots
        )
      }
    },
    isChevronRightDisabled() {
      return this.isTimeslotsLoading || this.noTimeslots
    },
  },
  watch: {
    isTimeslotsLoading(newVal, oldVal) {
      if (!oldVal && newVal) {
        this.hasTimeslots = null // reset
      }

      if (oldVal && !newVal && !this.timeslots.length) {
        this.hasTimeslots = false
      } else if (oldVal && !newVal && this.timeslots.length) {
        this.hasTimeslots = true
      }
    },
    noTimeslots() {
      // if no timeslots, jump to first available week with timeslots
      if (this.noTimeslots) {
        // timeslots[0].datum = first available timeslot in up to 14 days
        // datesWithTimeslots[0] = first available timeslot within the rest of the month
        // if later than that user will be told to open the calendar
        if (this.datesWithTimeslots[0] || this.timeslots[0]?.datum) {
          this.$store.commit(
            'setFirstDateOfWeek',
            getLastSunday(this.datesWithTimeslots[0] || this.timeslots[0]?.datum)
          )
        }
      }
    },
    dateColumns() {
      const uniqueDates = [...new Set(this.dateColumns.map((date) => date.slice(0, -3)))]
      this.$emit('monthYearArray', uniqueDates)
    },
  },
  methods: {
    minutesToHHMM,
    convertDateHeader(date) {
      const weekday = new Date(date).toLocaleDateString('de-AT', {
        weekday: 'short',
      })
      const digits = new Date(date).getDate().toLocaleString('de-AT', { minimumIntegerDigits: 2 })
      return `${weekday}. ${digits}`
    },
    columnTimeslots(date) {
      const columnTimeslots = this.unstackedTimeslots.filter((timeslot) => timeslot.datum === date)

      return clusterTimeslots(columnTimeslots)
    },
    toPreviousColumns(e) {
      if (e.detail && e.detail !== 1) return // do not allow multiple clicks

      if (this.isDayFiltersSet) {
        this.$store.commit('setDayFiltersStartDatesIndex', this.dayFiltersStartDatesIndex - 1)
        this.$store.commit(
          'setFirstDateOfWeek',
          getLastSunday(this.dayFiltersStartDates[this.dayFiltersStartDatesIndex])
        )
      } else {
        this.$store.commit('setFirstDateOfWeek', getPreviousDate(this.firstDateOfWeek, 7))
      }
    },
    toNextColumns(e) {
      if (e.detail && e.detail !== 1) return // do not allow multiple clicks

      if (this.isDayFiltersSet) {
        const sundayAfterLastDate = getNextSunday(this.dateColumns[this.dateColumns.length - 1])
        if (!this.dayFiltersStartDates.includes(sundayAfterLastDate)) {
          this.$store.commit('setDayFiltersStartDates', [
            ...this.dayFiltersStartDates,
            sundayAfterLastDate,
          ])
        }
        this.$store.commit('setDayFiltersStartDatesIndex', this.dayFiltersStartDatesIndex + 1)
        this.$store.commit('setFirstDateOfWeek', sundayAfterLastDate)
      } else {
        this.$store.commit('setFirstDateOfWeek', getNextDate(this.firstDateOfWeek, 7))
      }
    },
    isDropdownSelected(timeslot) {
      return (
        this.selectedTimeslot &&
        timeslot.map((slot) => slot.datum).includes(this.selectedTimeslot[0]?.datum) &&
        timeslot.map((slot) => slot.von).includes(this.selectedTimeslot[0]?.von)
      )
    },
    isOverlappingTimeslot(timeslot) {
      return this.overlappingTimeslots.some(
        (overlappingTimeslot) =>
          overlappingTimeslot.datum === timeslot.datum && overlappingTimeslot.von === timeslot.von
      )
    },
    isSelectedTimeslot(timeslot) {
      return (
        this.selectedTimeslot &&
        this.selectedTimeslot[0]?.datum === timeslot.datum &&
        this.selectedTimeslot[0]?.von === timeslot.von
      )
    },
    handleSelectTimeslot(timeslot) {
      if (timeslot.stackId) {
        const timeslots = this.filteredTimeslots.filter(
          (element) => element.stackId === timeslot.stackId
        )
        this.$store.commit('setSelectedTimeslot', timeslots)
      } else {
        this.$store.commit('setSelectedTimeslot', [timeslot])
      }
    },
  },
}
</script>

<style lang="scss">
.timeslots-desktop {
  display: flex;
  flex-direction: column;
  position: relative;

  &__chevron {
    cursor: pointer;
    position: absolute;
    top: 3px;
    background: none;
    border: none;
    padding: 8px;

    &:focus-visible {
      outline: 2px solid $color-info-primary;
      border-radius: 4px;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &--disabled {
      pointer-events: none;
    }
  }

  &__chevron-left {
    left: -48px;

    @media (max-width: $breakpoint-max-tablet) {
      left: -28px;
    }
  }

  &__chevron-right {
    right: -48px;

    @media (max-width: $breakpoint-max-tablet) {
      right: -28px;
    }
  }

  &__data {
    display: flex;
    justify-content: space-between;
    gap: 8px;
  }

  &__column {
    display: flex;
    flex-direction: column;
    flex: 1;

    #content &__header,
    &__header {
      background-color: $color-black;
      border-radius: 4px;
      color: white;
      font-size: 12px;
      font-weight: $font-weight-bold;
      margin-bottom: 8px;
      padding: 6px 0;
      position: relative;
      text-align: center;
      text-transform: uppercase;
      margin-top: 0;
    }

    &__body {
      display: flex;
      flex-direction: column;
      flex: 1;
      gap: 4px;
      font-weight: $font-weight-medium;
      border: none;
      padding: 0;
      margin: 0;

      .el-popper__arrow {
        display: none;
      }

      &__radio {
        display: block;
      }

      &__radio-input:focus-visible + &__slot {
        outline: 2px solid $color-info-primary;
      }

      &__slot {
        @include flex-center;
        background-color: $color-grey-light;
        border-radius: 4px;
        cursor: pointer;
        gap: 4px;
        padding: 8px 0;
        width: 100%;
        border: none;
        font: inherit;
        text-align: center;

        &:hover:not(&--selected) {
          background-color: $color-grey-dark;
        }

        &:focus-visible {
          outline: 2px solid $color-info-primary;
        }

        &--active {
          background-color: $color-grey-dark;
        }

        &--selected {
          background-color: $color-yellow-primary;
        }

        &--error {
          border: 1px solid $color-danger-primary;
        }

        &--prefix,
        &--suffix {
          color: $color-text-secondary;
          font-size: 12px;
          font-weight: $font-weight-regular;
        }
      }

      &__filler {
        background-color: $color-grey-light;
        border-radius: 4px;
        flex: 1;
        font-weight: $font-weight-medium;
        opacity: 0.3;
      }
    }
  }

  &__empty {
    @include flex-center;
    flex-direction: column;
    background-color: rgba($color-grey-light, 0.3);
    border-radius: 4px;
    color: $color-text-secondary;
    height: 200px;
    text-align: center;

    svg {
      margin-bottom: 12px;
    }

    &__text {
      max-width: 300px;

      &--highlight {
        font-weight: $font-weight-medium;
      }
    }
  }
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
